import { supabase } from '../lib/supabase';
import uploadService from './uploadService';

/**
 * Upload Processing Service - Extracts content from uploads and creates training modules/scripts
 * Uses Puter AI for content extraction and module generation
 */
const uploadProcessingService = {
  /**
   * Process an uploaded file - extract content and create training materials
   * @param {string} uploadId - The upload ID to process
   * @returns {Promise<Object>} Processing result
   */
  async processUpload(uploadId) {
    try {
      // Get upload record
      const upload = await uploadService.getUploadById(uploadId);

      if (upload.processing_status === 'completed') {
        return { success: true, message: 'Already processed' };
      }

      // Update status to processing
      await uploadService.updateProcessingStatus(uploadId, 'processing');

      // Download and extract text from file
      const extractedText = await this.extractTextFromFile(upload);

      if (!extractedText || extractedText.trim().length < 50) {
        throw new Error('Insufficient content extracted from file');
      }

      // Analyze content with AI
      const analysis = await this.analyzeContent(extractedText, upload.file_type);

      // Create appropriate content based on file type
      let createdScriptId = null;
      let createdModuleId = null;

      if (upload.file_type === 'script') {
        createdScriptId = await this.createScriptFromContent(analysis, upload.team_id);
      } else if (upload.file_type === 'training_material' || upload.file_type === 'objection_guide') {
        createdModuleId = await this.createTrainingModule(analysis, upload);
      }

      // Update upload record with results
      await uploadService.updateProcessingStatus(uploadId, 'completed', {
        extracted_text: extractedText,
        extracted_metadata: analysis,
        created_script_id: createdScriptId,
        created_training_module_id: createdModuleId
      });

      return {
        success: true,
        scriptId: createdScriptId,
        moduleId: createdModuleId
      };

    } catch (error) {
      console.error('Processing error:', error);

      // Update status to failed
      await uploadService.updateProcessingStatus(uploadId, 'failed', {
        processing_error: error.message
      });

      throw error;
    }
  },

  /**
   * Extract text content from uploaded file
   * @param {Object} upload - Upload record
   * @returns {Promise<string>} Extracted text
   */
  async extractTextFromFile(upload) {
    try {
      const blob = await uploadService.downloadFile(upload.file_path);

      // For text files, read directly
      if (upload.mime_type === 'text/plain') {
        return await blob.text();
      }

      // For PDFs and DOCX, use Puter AI's document parsing
      if (window.puter && window.puter.ai) {
        const formData = new FormData();
        formData.append('file', blob, upload.file_name);

        // Use Puter AI to extract text from document
        const response = await window.puter.ai.chat(
          `Extract all text content from this document. Return only the extracted text without any additional commentary or formatting.`,
          {
            files: [blob]
          }
        );

        return response.message?.content || '';
      }

      // Fallback: use basic text extraction
      return await this.fallbackTextExtraction(blob, upload.mime_type);

    } catch (error) {
      console.error('Text extraction error:', error);
      throw new Error('Failed to extract text from file');
    }
  },

  /**
   * Fallback text extraction for when Puter AI is unavailable
   * @param {Blob} blob - File blob
   * @param {string} mimeType - MIME type
   * @returns {Promise<string>} Extracted text
   */
  async fallbackTextExtraction(blob, mimeType) {
    // For now, return a message indicating manual review needed
    // In production, you could integrate pdf.js or mammoth.js here
    return `[Content extraction requires manual review for ${mimeType} files. Please ensure Puter AI is available for automatic processing.]`;
  },

  /**
   * Analyze extracted content with AI
   * @param {string} text - Extracted text
   * @param {string} fileType - File type
   * @returns {Promise<Object>} Analysis result
   */
  async analyzeContent(text, fileType) {
    try {
      if (!window.puter || !window.puter.ai) {
        throw new Error('Puter AI not available');
      }

      const prompt = this.getAnalysisPrompt(text, fileType);

      const response = await window.puter.ai.chat(prompt);

      // Parse JSON response
      const content = response.message?.content || '{}';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      
      if (!jsonMatch) {
        throw new Error('Invalid AI response format');
      }

      return JSON.parse(jsonMatch[0]);

    } catch (error) {
      console.error('Content analysis error:', error);
      
      // Return basic analysis as fallback
      return {
        title: 'Imported Material',
        summary: text.substring(0, 200) + '...',
        keyTopics: this.extractBasicTopics(text),
        difficulty: 'intermediate',
        sections: [{
          title: 'Main Content',
          content: text
        }]
      };
    }
  },

  /**
   * Get AI analysis prompt based on file type
   * @param {string} text - Extracted text
   * @param {string} fileType - File type
   * @returns {string} Prompt
   */
  getAnalysisPrompt(text, fileType) {
    const basePrompt = `Analyze the following sales training material and extract structured information.

Content:
${text.substring(0, 4000)}

Return ONLY a JSON object with this exact structure:
{
  "title": "descriptive title",
  "summary": "2-3 sentence summary",
  "keyTopics": ["topic1", "topic2", "topic3"],
  "difficulty": "beginner|intermediate|advanced",
  "learningObjectives": ["objective1", "objective2"],
  "sections": [
    {
      "title": "section title",
      "content": "section content",
      "scenarios": ["scenario1", "scenario2"]
    }
  ]
}`;

    if (fileType === 'script') {
      return basePrompt + `\n\nFocus on identifying: script structure, key talking points, objection responses, closing techniques.`;
    } else if (fileType === 'objection_guide') {
      return basePrompt + `\n\nFocus on identifying: objection types, response frameworks, best practices.`;
    } else {
      return basePrompt + `\n\nFocus on identifying: training concepts, practical applications, key methodologies.`;
    }
  },

  /**
   * Extract basic topics from text (fallback method)
   * @param {string} text - Text content
   * @returns {Array<string>} Topics
   */
  extractBasicTopics(text) {
    const keywords = [
      'objection', 'closing', 'prospecting', 'cold-calling', 
      'negotiation', 'value proposition', 'discovery', 'qualification'
    ];

    const lowerText = text.toLowerCase();
    return keywords.filter(keyword => lowerText.includes(keyword));
  },

  /**
   * Create a script from analyzed content
   * @param {Object} analysis - AI analysis
   * @param {string} teamId - Team ID
   * @returns {Promise<string>} Created script ID
   */
  async createScriptFromContent(analysis, teamId) {
    try {
      // Extract script content from sections
      const scriptContent = analysis.sections
        .map(section => section.content)
        .join('\n\n');

      const { data, error } = await supabase
        .from('scripts')
        .insert({
          team_id: teamId,
          title: analysis.title || 'Imported Script',
          content: scriptContent,
          category: 'custom',
          tags: analysis.keyTopics || [],
          is_custom: true,
          created_from_upload: true
        })
        .select()
        .single();

      if (error) throw error;

      return data.id;

    } catch (error) {
      console.error('Script creation error:', error);
      return null;
    }
  },

  /**
   * Create a custom training module from analyzed content
   * @param {Object} analysis - AI analysis
   * @param {Object} upload - Upload record
   * @returns {Promise<string>} Created module ID
   */
  async createTrainingModule(analysis, upload) {
    try {
      // Format content for training module
      const moduleContent = {
        type: 'custom_training',
        source: 'user_upload',
        raw_content: analysis
      };

      // Format sections for training
      const sections = analysis.sections?.map((section, idx) => ({
        id: `section_${idx + 1}`,
        title: section.title,
        content: section.content,
        scenarios: section.scenarios || [],
        order: idx + 1
      })) || [];

      const { data, error } = await supabase
        .from('custom_training_modules')
        .insert({
          team_id: upload.team_id,
          source_material_id: upload.id,
          title: analysis.title || upload.file_name,
          description: analysis.summary || '',
          content: moduleContent,
          difficulty_level: analysis.difficulty || 'intermediate',
          estimated_duration: Math.ceil((analysis.sections?.length || 1) * 15), // 15 min per section
          sections: sections,
          learning_objectives: analysis.learningObjectives || [],
          key_topics: analysis.keyTopics || [],
          is_published: true // Auto-publish for team
        })
        .select()
        .single();

      if (error) throw error;

      return data.id;

    } catch (error) {
      console.error('Training module creation error:', error);
      return null;
    }
  },

  /**
   * Get custom training modules for a team
   * @param {string} teamId - Team ID
   * @returns {Promise<Array>} Training modules
   */
  async getCustomTrainingModules(teamId) {
    try {
      const { data, error } = await supabase
        .from('custom_training_modules')
        .select(`
          *,
          source_material:source_material_id (
            id,
            file_name,
            file_type
          )
        `)
        .eq('team_id', teamId)
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];

    } catch (error) {
      console.error('Error fetching custom modules:', error);
      return [];
    }
  },

  /**
   * Get user progress for a custom training module
   * @param {string} userId - User ID
   * @param {string} moduleId - Module ID
   * @returns {Promise<Object>} Progress record
   */
  async getModuleProgress(userId, moduleId) {
    try {
      const { data, error } = await supabase
        .from('custom_training_progress')
        .select('*')
        .eq('user_id', userId)
        .eq('module_id', moduleId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      return data || null;

    } catch (error) {
      console.error('Error fetching progress:', error);
      return null;
    }
  },

  /**
   * Update user progress for a custom training module
   * @param {string} userId - User ID
   * @param {string} moduleId - Module ID
   * @param {Object} progressData - Progress data
   * @returns {Promise<Object>} Updated progress
   */
  async updateModuleProgress(userId, moduleId, progressData) {
    try {
      const { data, error } = await supabase
        .from('custom_training_progress')
        .upsert({
          user_id: userId,
          module_id: moduleId,
          ...progressData,
          last_accessed_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;

      return data;

    } catch (error) {
      console.error('Error updating progress:', error);
      throw error;
    }
  }
};

export default uploadProcessingService;
